import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'
import { useShallow } from 'zustand/react/shallow'

export type SelectorBasket = {
  id: string
  friendlyId?: string | null
  customName?: string | null
  // Schema field is non-nullable (Boolean!) — optional here only because several call sites
  // (handleSelectBasket's `{ id: basketId }` fallback, guest-basket bootstrapping) construct
  // partial SelectorBasket objects that never fetch this field at all.
  isCateringTeamOrder?: boolean
}

export type BasketSelectionConfig = {
  isAuthenticatedUser: boolean
  vendorId?: string
  initialSelectedId?: string
  preventAutomaticSelection?: boolean
  accountId?: string | number
  // The visit arrived via a team-order share link (?groupOrder=1) — its basket is someone
  // else's, so by-id resolution must not depend on the viewer's own basket list.
  isCateringTeamOrderUrl?: boolean
}

export type BasketSelectionState = {
  // State
  selectedBasket: SelectorBasket | null
  orderedUnselectedBaskets: Array<SelectorBasket>
  deletedBaskets: Array<string>
  loading: boolean
  creating: boolean
  justCreated: boolean
  hasUsedInitialSelection: boolean

  // Baskets storage
  authenticatedBaskets: Array<SelectorBasket>
  unauthenticatedBaskets: Array<SelectorBasket>

  // Config
  config: BasketSelectionConfig

  // Actions
  setSelectedBasket: (basket: SelectorBasket | null) => void
  setOrderedUnselectedBaskets: (baskets: Array<SelectorBasket>) => void
  addDeletedBasket: (basketId: string) => void
  removeDeletedBasket: (basketId: string) => void
  setLoading: (loading: boolean) => void
  setCreating: (creating: boolean) => void
  setJustCreated: (justCreated: boolean) => void
  setHasUsedInitialSelection: (hasUsed: boolean) => void
  setConfig: (config: BasketSelectionConfig) => void

  // Baskets actions
  setAuthenticatedBaskets: (
    baskets: Array<SelectorBasket> | ((prev: Array<SelectorBasket>) => Array<SelectorBasket>),
  ) => void
  setUnauthenticatedBaskets: (
    baskets: Array<SelectorBasket> | ((prev: Array<SelectorBasket>) => Array<SelectorBasket>),
  ) => void

  // Reset
  reset: () => void
}

const initialState = {
  selectedBasket: null,
  orderedUnselectedBaskets: [],
  deletedBaskets: [],
  loading: true,
  creating: false,
  justCreated: false,
  hasUsedInitialSelection: false,
  authenticatedBaskets: [],
  unauthenticatedBaskets: [],
  config: {
    isAuthenticatedUser: false,
    vendorId: undefined,
    initialSelectedId: undefined,
    preventAutomaticSelection: false,
    accountId: undefined,
  },
}

const useBasketSelectionStore = create<BasketSelectionState>()(
  subscribeWithSelector((set, get) => ({
    ...initialState,

    // Actions
    setSelectedBasket: (basket) => set({ selectedBasket: basket }),

    setOrderedUnselectedBaskets: (baskets) => set({ orderedUnselectedBaskets: baskets }),

    addDeletedBasket: (basketId) => {
      const current = get().deletedBaskets
      if (!current.includes(basketId)) {
        set({ deletedBaskets: [...current, basketId] })
      }
    },

    removeDeletedBasket: (basketId) => {
      const current = get().deletedBaskets
      set({ deletedBaskets: current.filter((id) => id !== basketId) })
    },

    setLoading: (loading) => set({ loading }),
    setCreating: (creating) => set({ creating }),
    setJustCreated: (justCreated) => set({ justCreated }),
    setHasUsedInitialSelection: (hasUsed) => set({ hasUsedInitialSelection: hasUsed }),
    setConfig: (config) => set({ config }),

    // Baskets actions
    setAuthenticatedBaskets: (baskets) => {
      if (typeof baskets === 'function') {
        const current = get().authenticatedBaskets
        set({ authenticatedBaskets: baskets(current) })
      } else {
        set({ authenticatedBaskets: baskets })
      }
    },

    setUnauthenticatedBaskets: (baskets) => {
      if (typeof baskets === 'function') {
        const current = get().unauthenticatedBaskets
        set({ unauthenticatedBaskets: baskets(current) })
      } else {
        set({ unauthenticatedBaskets: baskets })
      }
    },

    // Reset
    reset: () => set(initialState),
  })),
)

export const useBasketSelectionState = () =>
  useBasketSelectionStore(
    useShallow((state) => ({
      selectedBasket: state.selectedBasket,
      orderedUnselectedBaskets: state.orderedUnselectedBaskets,
      deletedBaskets: state.deletedBaskets,
      loading: state.loading,
      creating: state.creating,
      justCreated: state.justCreated,
      hasUsedInitialSelection: state.hasUsedInitialSelection,
      authenticatedBaskets: state.authenticatedBaskets,
      unauthenticatedBaskets: state.unauthenticatedBaskets,
      config: state.config,
    })),
  )

export const useBasketSelectionActions = () => {
  const setSelectedBasket = useBasketSelectionStore((state) => state.setSelectedBasket)
  const setOrderedUnselectedBaskets = useBasketSelectionStore(
    (state) => state.setOrderedUnselectedBaskets,
  )
  const addDeletedBasket = useBasketSelectionStore((state) => state.addDeletedBasket)
  const removeDeletedBasket = useBasketSelectionStore((state) => state.removeDeletedBasket)
  const setLoading = useBasketSelectionStore((state) => state.setLoading)
  const setCreating = useBasketSelectionStore((state) => state.setCreating)
  const setJustCreated = useBasketSelectionStore((state) => state.setJustCreated)
  const setHasUsedInitialSelection = useBasketSelectionStore(
    (state) => state.setHasUsedInitialSelection,
  )
  const setConfig = useBasketSelectionStore((state) => state.setConfig)
  const setAuthenticatedBaskets = useBasketSelectionStore((state) => state.setAuthenticatedBaskets)
  const setUnauthenticatedBaskets = useBasketSelectionStore(
    (state) => state.setUnauthenticatedBaskets,
  )
  const reset = useBasketSelectionStore((state) => state.reset)

  return {
    setSelectedBasket,
    setOrderedUnselectedBaskets,
    addDeletedBasket,
    removeDeletedBasket,
    setLoading,
    setCreating,
    setJustCreated,
    setHasUsedInitialSelection,
    setConfig,
    setAuthenticatedBaskets,
    setUnauthenticatedBaskets,
    reset,
  }
}
